/** Storage unit sizes — one entry per size people actually search for.
 *
 *  Why this exists, and why it is shaped this way.
 *
 *  We had 993 tool pages built as 19 templates × 52 states, and Google
 *  Keyword Planner reports **zero** monthly searches for the state axis:
 *  "storage unit size calculator texas", "self storage tax by state",
 *  "towing rules by state" and "security deposit cap by state" all return 0.
 *  Multiplying a page by a dimension nobody searches produces 993 near-identical
 *  pages and no traffic.
 *
 *  The sibling product that works, Casetop, multiplies on real entities
 *  instead — 203 individual dental codes, 32 individual teeth — and each page
 *  answers one query somebody genuinely types. "teeth numbers" alone is 49,500
 *  a month, against 370 for "dental lab software". That is how a smaller market
 *  produced 32 organic sign-ups since August while we produced 7.
 *
 *  Storage has the same shape of demand, on the size axis, and we had none of
 *  it. The figures in `searches` are US averages from Keyword Planner, pulled
 *  2026-10-22:
 *
 *    storage unit sizes / self storage dimensions   3,600
 *    5x10                                           2,400
 *    10x10                                          1,900
 *    10x20                                            880
 *    10x15                                            720
 *    5x15 / 5x5                                       390
 *
 *  `searches` is recorded so the next person can tell a page that earns its
 *  place from one that does not, and prune accordingly. It is not displayed.
 */

export type UnitSize = {
  /** URL slug and the way people write it in a search box. */
  slug: string;
  /** Display label with the proper multiplication sign. */
  label: string;
  widthFt: number;
  depthFt: number;
  /** US average monthly searches for this size, Keyword Planner 2026-10-22. */
  searches: number;
  /** The comparison people reach for first. */
  comparison: string;
  /** What genuinely fits. Written to be useful, not to pad the page. */
  fits: string[];
  /** Honest ceiling — the thing people wrongly expect to fit. */
  doesNotFit: string;
  /** Typical US monthly asking rent. A range, because it varies by metro. */
  priceLow: number;
  priceHigh: number;
};

export const UNIT_SIZES: UnitSize[] = [
  {
    slug: "5x5",
    label: "5×5",
    widthFt: 5,
    depthFt: 5,
    searches: 390,
    comparison: "about the footprint of a hall cupboard",
    fits: [
      "Boxes, files and small bins stacked to head height",
      "A chest of drawers plus a few suitcases",
      "Seasonal kit — skis, a bike on its end, camping gear",
    ],
    doesNotFit: "A mattress laid flat, or anything from a full room",
    priceLow: 40,
    priceHigh: 75,
  },
  {
    slug: "5x10",
    label: "5×10",
    widthFt: 5,
    depthFt: 10,
    searches: 2400,
    comparison: "about the footprint of a small walk-in wardrobe",
    fits: [
      "A studio or one-bedroom flat's worth of furniture",
      "A mattress and bed frame stood on edge, plus boxes",
      "A sofa, a chair and a dozen boxes",
      "Motorbike with room left over",
    ],
    doesNotFit: "The contents of a two-bedroom home, or a car",
    priceLow: 55,
    priceHigh: 110,
  },
  {
    slug: "10x10",
    label: "10×10",
    widthFt: 10,
    depthFt: 10,
    searches: 1900,
    comparison: "about half a single garage",
    fits: [
      "A two-bedroom flat, furniture included",
      "Washer, dryer, fridge and a full lounge set",
      "Boxed office contents for a small team",
    ],
    doesNotFit: "A car with doors you can open, or a three-bedroom house",
    priceLow: 90,
    priceHigh: 170,
  },
  {
    slug: "10x15",
    label: "10×15",
    widthFt: 10,
    depthFt: 15,
    searches: 720,
    comparison: "a single garage you can still walk through",
    fits: [
      "A two or three-bedroom home with appliances",
      "A car, with boxes stacked down one side",
      "Trade stock, ladders and racking",
    ],
    doesNotFit: "Two vehicles, or a four-bedroom house in one load",
    priceLow: 120,
    priceHigh: 220,
  },
  {
    slug: "10x20",
    label: "10×20",
    widthFt: 10,
    depthFt: 20,
    searches: 880,
    comparison: "a standard single garage",
    fits: [
      "A three or four-bedroom house, appliances and all",
      "A full-size car or small van with the door shut",
      "A small business's entire stock and shelving",
    ],
    doesNotFit: "Two cars side by side",
    priceLow: 150,
    priceHigh: 280,
  },
  {
    slug: "10x25",
    label: "10×25",
    widthFt: 10,
    depthFt: 25,
    searches: 260,
    comparison: "a long single garage",
    fits: [
      "A four-bedroom house with the garage contents too",
      "A truck, a boat on a trailer, or a long vehicle",
      "Contract plant and materials between jobs",
    ],
    doesNotFit: "Anything needing width rather than length — it is still 10ft across",
    priceLow: 180,
    priceHigh: 320,
  },
  {
    slug: "10x30",
    label: "10×30",
    widthFt: 10,
    depthFt: 30,
    searches: 320,
    comparison: "the largest unit most sites offer",
    fits: [
      "A five-bedroom house in a single move",
      "A vehicle plus a full household",
      "Commercial inventory with aisles you can actually use",
    ],
    doesNotFit: "A wide load — the 10ft width is the real limit, not the 30ft depth",
    priceLow: 220,
    priceHigh: 400,
  },
  {
    slug: "20x20",
    label: "20×20",
    widthFt: 20,
    depthFt: 20,
    searches: 170,
    comparison: "a double garage",
    fits: [
      "Two vehicles side by side, doors opening",
      "A large house plus a workshop",
      "Pallet racking with a forklift lane",
    ],
    doesNotFit: "Little, at this size — the question is usually price, not space",
    priceLow: 300,
    priceHigh: 550,
  },
];

export const BY_SLUG: Record<string, UnitSize> = Object.fromEntries(
  UNIT_SIZES.map((u) => [u.slug, u]),
);

export const sqFt = (u: UnitSize) => u.widthFt * u.depthFt;
